import React from "react";
import { useSelector } from "react-redux";
import { Draw as DrawIcon, TaskAlt as TaskAltIcon } from "@mui/icons-material";

import FooterButton, { ProcessButton, UnprocessButton } from "./FooterButton";
import NoteSaveHint from "../NoteSaveHint";
import useNoteSaveHandlers from "../../hooks/useNoteSaveHandlers";
import useNoteButtonActionInProgress from "../../hooks/useNoteButtonActionInProgress";

import {
  USER_ROLES,
  SIGN_AND_PROCESS_SHORTCUT_EXCLUDED_CLINICS,
} from "static/constants";
import { getCurrentUserRole } from "utils/currentUser.util";
import useCtrlSpaceShortcut from "hooks/useCtrlSpaceShortcut";
import { isReportSigned } from "utils/footerView.util";
import { getProviderNoteTitle } from "utils/report";

const PhysicianReviewButtons = ({ handleProcess, handleSignProcess }) => {
  const notes = useNoteSaveHandlers();
  const isActionInProgress = useNoteButtonActionInProgress();

  const { accountsMapper } = useSelector((state) => state.account);
  const {
    nurseFlag,
    reportDetail,
    isSignAndProcessing,
    isSignAndUnProcessing,
    clinicNoteSubmittingAndSigningAndProcessing,
    clinicNoteSubmittingAndSigningAndUnProcessing,
    providerNoteSubmittingAndSigningAndProcessing,
    providerNoteSubmittingAndSigningAndUnProcessing,
  } = useSelector((state) => state.transmission);

  const isSigned = isReportSigned(reportDetail);

  const isShortcutExcluded =
    SIGN_AND_PROCESS_SHORTCUT_EXCLUDED_CLINICS.includes(reportDetail?.account);

  // savePhysicianNote saves the provider note when it is shown, else the clinic note
  const savedNoteName = accountsMapper?.[reportDetail?.account]?.settings
    ?.show_clinic_notes_with_provider_notes
    ? getProviderNoteTitle(accountsMapper, reportDetail?.account)
    : "Clinic Notes";

  const handleSaveAndSign = (processState) =>
    notes.savePhysicianNote(true, true, processState) ||
    handleSignProcess(processState);

  useCtrlSpaceShortcut(
    isShortcutExcluded || isSigned || nurseFlag || isActionInProgress
      ? null
      : () => handleSaveAndSign("process")
  );

  if (isSigned) {
    return (
      <>
        <UnprocessButton onClick={() => handleProcess("unprocess")} />
        <ProcessButton onClick={() => handleProcess("process")} />
      </>
    );
  }

  return (
    <>
      <div className="view-pdf__notes-footer__group">
        <FooterButton
          kind="outlined"
          label="Sign & Unprocess"
          icon={<DrawIcon />}
          dataAction="sign-unprocess-button"
          onClick={() => handleSaveAndSign("unprocess")}
          isLoading={
            isSignAndUnProcessing ||
            clinicNoteSubmittingAndSigningAndUnProcessing ||
            providerNoteSubmittingAndSigningAndUnProcessing
          }
        />

        <FooterButton
          label="Sign & Process"
          icon={<TaskAltIcon />}
          dataAction="sign-process-button"
          onClick={() => handleSaveAndSign("process")}
          isLoading={
            isSignAndProcessing ||
            clinicNoteSubmittingAndSigningAndProcessing ||
            providerNoteSubmittingAndSigningAndProcessing
          }
          disabled={nurseFlag}
        />
      </div>

      <NoteSaveHint>
        Signing this report will also save the {savedNoteName}.
      </NoteSaveHint>
    </>
  );
};

const ClinicUserReviewButtons = ({ handleProcess }) => {
  const userRole = getCurrentUserRole();
  const { nurseFlag } = useSelector((state) => state.transmission);

  return (
    <>
      <UnprocessButton onClick={() => handleProcess("unprocess")} />
      <ProcessButton
        onClick={() => handleProcess("process")}
        disabled={nurseFlag && userRole === USER_ROLES.CLINIC}
      />
    </>
  );
};

const ClinicReviewButtons = (props) => {
  const isPhysician = getCurrentUserRole() === USER_ROLES.PHYSICIAN;

  return isPhysician ? (
    <PhysicianReviewButtons {...props} />
  ) : (
    <ClinicUserReviewButtons {...props} />
  );
};

export default ClinicReviewButtons;
